import { useEffect, useState } from "react";
import { api } from "./lib/api";

type Device = {
  app_user_id: string; name: string; platform: string | null; build: string | null;
  seen: string | null; model: string | null; os: string | null; webview: string | null;
  install_source: string | null; storage_used_mb: number | null; storage_quota_mb: number | null;
  bundle_version: string | null; bundle_id: string | null; bundle_source: string | null;
  native: string | null; channel: string | null; channel_status: string | null;
  channel_error: string | null; last_failed_version: string | null; last_failed_at: string | null;
};

function ago(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms)) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── WHAT EACH PHONE IS ACTUALLY RUNNING, AND ON WHAT ────────────────────────────────────────────
//
// Every question asked during the live-update investigation of 2026-09-18 was answerable from rows
// field_event already held, and not one of them could be asked from a screen. Which bundle is that
// phone on. Did it arrive over the air or come out of the store build. Is it attached to a channel
// at all. Why is it two versions behind. Each took a hand-written query against the database, and
// the answers had been recorded since August by a beacon built after the LAST time a live update
// reached nobody.
//
// THE COLUMN THAT MATTERS MOST IS "Live update", and it is not the version number. A phone running
// store JavaScript and a phone running a live update report the SAME version string; telling them
// apart is the difference between "shipped" and "shipped to nobody", and it cost an hour on
// 2026-09-18 and four days on 2026-09-03. The rule is the plugin's own, quoted in
// app/src/lib/bundleIdentity.ts: a bundle id of exactly "builtin" is the store build's own code.
//
// AN ERROR IS SHOWN AS AN ERROR, never as a blank. `no_channel` is the exact string that went
// unread for four days; a phone in that state must LOOK wrong here, not merely lack a tick.
export function Devices() {
  const [rows, setRows] = useState<Device[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const load = () => {
    setBusy(true); setErr(null);
    api<{ devices: Device[] }>("/field-devices")
      .then((d) => setRows(d.devices))
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold">Field devices</h2>
        <button onClick={load} disabled={busy}
          className="text-sm rounded-lg border border-border px-3 py-1 disabled:opacity-50">
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>

      <p className="text-sm text-muted max-w-3xl">
        The newest self-report from each phone, sent on every launch. <strong>Live update</strong> is
        the one to read: a phone on store code and a phone on a live update show the same version
        number, and only this column tells them apart.
      </p>

      {/* A FAILURE IS AN ERROR, NOT AN ABSENCE — the rule empty-vs-error-guard.sh exists for. */}
      {err && (
        <div className="err">
          Couldn't load devices. {err}
        </div>
      )}

      {!err && rows && rows.length === 0 && (
        <div className="rounded-xl border border-border px-4 py-3 text-sm text-muted">
          No phone has reported in the last 30 days.
        </div>
      )}

      {!err && rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-muted border-b border-border">
                <th className="py-2 pr-4 font-medium">Crew</th>
                <th className="py-2 pr-4 font-medium">Phone</th>
                <th className="py-2 pr-4 font-medium">App version</th>
                <th className="py-2 pr-4 font-medium">Live update</th>
                <th className="py-2 pr-4 font-medium">Channel</th>
                <th className="py-2 pr-4 font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.app_user_id} className="border-b border-border/60 align-top">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{d.name}</div>
                    {d.last_failed_version && (
                      <div className="text-xs text-red-700">
                        failed to fetch {d.last_failed_version} · {ago(d.last_failed_at)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <div>{d.model ?? "—"}</div>
                    <div className="text-xs text-muted">
                      {[d.os, d.platform, d.install_source].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">{d.build ?? "—"}</td>
                  <td className="py-2 pr-4">
                    <div className="font-mono text-xs">{d.bundle_version ?? "—"}</div>
                    <div className="text-xs">
                      {d.bundle_source === "ota"
                        ? <span className="text-emerald-700">over the air</span>
                        : d.bundle_source === "builtin"
                          ? <span className="text-muted">built in</span>
                          : <span className="text-amber-700">unknown</span>}
                      {d.native && <span className="text-muted"> · shell {d.native}</span>}
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <div>{d.channel ?? "—"}</div>
                    {d.channel_error
                      ? <div className="text-xs text-red-700">{d.channel_error}</div>
                      : d.channel_status && <div className="text-xs text-muted">{d.channel_status}</div>}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{ago(d.seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
